package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.util.ImageHeaders
import kotlin.math.max

/**
 * Página PDF simples (Helvetica WinAnsi + logo JPEG opcional) para os documentos da
 * Emissão de Documentos. Coordenadas em pontos, origem no canto inferior esquerdo.
 */
internal class PdfPagina(private val largura: Double, private val altura: Double, private val logo: ByteArray?) {
    private val pages = mutableListOf<String>()
    private val ops = StringBuilder()
    val logoSize = logo?.let { ImageHeaders.size(it) }
    private val imagens = mutableListOf<ByteArray>()

    /** Registra um JPEG extra (ex.: fundo de página) e devolve o índice para [imagem]; null se inválido. */
    fun registrarImagem(jpeg: ByteArray): Int? {
        if (ImageHeaders.size(jpeg) == null) return null
        imagens += jpeg
        return imagens.size - 1
    }

    fun imagem(indice: Int, x: Double, bottom: Double, w: Double, h: Double) {
        if (indice !in imagens.indices) return
        ops.append("q\n${f(w)} 0 0 ${f(h)} ${f(x)} ${f(bottom)} cm\n/ImX${indice + 1} Do\nQ\n")
    }

    fun newPage() {
        if (ops.isNotEmpty()) pages += ops.toString()
        ops.clear()
    }

    fun logo(x: Double, bottom: Double, w: Double, h: Double) {
        if (logo == null || logoSize == null) return
        ops.append("q\n${f(w)} 0 0 ${f(h)} ${f(x)} ${f(bottom)} cm\n/Im1 Do\nQ\n")
    }

    fun rect(x: Double, bottom: Double, w: Double, h: Double, lw: Double = 0.6) {
        ops.append("q\n${f(lw)} w\n0.2 0.2 0.2 RG\n${f(x)} ${f(bottom)} ${f(w)} ${f(h)} re\nS\nQ\n")
    }

    fun fill(x: Double, bottom: Double, w: Double, h: Double, rgb: Int) {
        ops.append("q\n${rgbOp(rgb)} rg\n${f(x)} ${f(bottom)} ${f(w)} ${f(h)} re\nf\nQ\n")
    }

    fun vline(x: Double, y1: Double, y2: Double) {
        ops.append("q\n0.6 w\n0.2 0.2 0.2 RG\n${f(x)} ${f(y1)} m\n${f(x)} ${f(y2)} l\nS\nQ\n")
    }

    fun hline(x1: Double, x2: Double, y: Double) {
        ops.append("q\n0.6 w\n0.2 0.2 0.2 RG\n${f(x1)} ${f(y)} m\n${f(x2)} ${f(y)} l\nS\nQ\n")
    }

    fun text(s: String, x: Double, baseline: Double, size: Double, bold: Boolean, rgb: Int = 0) {
        if (s.isEmpty()) return
        val font = if (bold) "/F2" else "/F1"
        ops.append("q\n${rgbOp(rgb)} rg\nBT\n$font ${f(size)} Tf\n${f(x)} ${f(baseline)} Td\n(${escape(s)}) Tj\nET\nQ\n")
    }

    fun textCentered(s: String, xa: Double, xb: Double, baseline: Double, size: Double, bold: Boolean, rgb: Int = 0) {
        val w = PdfTexto.largura(s, size, bold)
        text(s, xa + max(0.0, (xb - xa - w) / 2), baseline, size, bold, rgb)
    }

    fun finish(titulo: String): ByteArray {
        if (ops.isNotEmpty()) pages += ops.toString()
        ops.clear()
        return assemble(titulo)
    }

    private fun assemble(titulo: String): ByteArray {
        val temLogo = logo != null && logoSize != null
        val objs = linkedMapOf<Int, ByteArray>()
        val firstPage = if (temLogo) 6 else 5
        val kids = pages.indices.joinToString(" ") { "${firstPage + it * 2} 0 R" }
        objs[1] = latin1("<< /Type /Catalog /Pages 2 0 R >>")
        objs[2] = latin1("<< /Type /Pages /Kids [ $kids ] /Count ${pages.size} >>")
        objs[3] = latin1("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>")
        objs[4] = latin1("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>")
        if (temLogo) {
            objs[5] = latin1(
                "<< /Type /XObject /Subtype /Image /Width ${logoSize!!.width} /Height ${logoSize.height} " +
                    "/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${logo!!.size} >>\nstream\n"
            ) + logo + latin1("\nendstream")
        }
        val info = firstPage + pages.size * 2
        val total = info + imagens.size
        imagens.forEachIndexed { i, jpeg ->
            val s = ImageHeaders.size(jpeg)!!
            objs[info + 1 + i] = latin1(
                "<< /Type /XObject /Subtype /Image /Width ${s.width} /Height ${s.height} " +
                    "/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.size} >>\nstream\n"
            ) + jpeg + latin1("\nendstream")
        }
        val xobjItens = buildList {
            if (temLogo) add("/Im1 5 0 R")
            imagens.indices.forEach { add("/ImX${it + 1} ${info + 1 + it} 0 R") }
        }
        val xobj = if (xobjItens.isEmpty()) "" else "/XObject << ${xobjItens.joinToString(" ")} >> "
        pages.forEachIndexed { i, content ->
            val p = firstPage + i * 2
            objs[p] = latin1(
                "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${largura.toInt()} ${altura.toInt()}] /Contents ${p + 1} 0 R " +
                    "/Resources << /Font << /F1 3 0 R /F2 4 0 R >> $xobj>> >>"
            )
            val bytes = latin1(content)
            objs[p + 1] = latin1("<< /Length ${bytes.size} >>\nstream\n") + bytes + latin1("\nendstream")
        }
        objs[info] = latin1("<< /Producer (ICD-PdfBasico) /Title (${escape(titulo)}) >>")

        val out = ArrayList<Byte>(64 * 1024)
        fun add(b: ByteArray) { b.forEach { out.add(it) } }
        add(latin1("%PDF-1.4\n%\u00e2\u00e3\u00cf\u00d3\n"))
        val offsets = IntArray(total + 1)
        for (n in 1..total) {
            offsets[n] = out.size
            add(latin1("$n 0 obj\n"))
            add(objs[n] ?: latin1("<< >>"))
            add(latin1("\nendobj\n"))
        }
        val xref = out.size
        val sb = StringBuilder("xref\n0 ${total + 1}\n0000000000 65535 f \n")
        for (n in 1..total) sb.append(offsets[n].toString().padStart(10, '0')).append(" 00000 n \n")
        sb.append("trailer\n<< /Size ${total + 1} /Root 1 0 R /Info $info 0 R >>\nstartxref\n$xref\n%%EOF")
        add(latin1(sb.toString()))
        return out.toByteArray()
    }

    private fun f(v: Double): String {
        val r = kotlin.math.round(v * 100) / 100.0
        return if (r == r.toLong().toDouble()) r.toLong().toString() else r.toString()
    }

    private fun rgbOp(rgb: Int): String {
        val r = ((rgb shr 16) and 0xFF) / 255.0
        val g = ((rgb shr 8) and 0xFF) / 255.0
        val b = (rgb and 0xFF) / 255.0
        return "${f(r)} ${f(g)} ${f(b)}"
    }

    private fun toWinAnsi(ch: Char): Int {
        val code = ch.code
        if (code in 32..126 || code in 160..255) return code
        return when (ch) {
            '–' -> 150
            '—' -> 151
            '“' -> 147
            '”' -> 148
            '‘' -> 145
            '’' -> 146
            '•' -> 149
            '…' -> 133
            else -> '?'.code
        }
    }

    private fun escape(s: String): String = buildString(s.length + 8) {
        for (ch in s) {
            val c = toWinAnsi(ch)
            when {
                c == '\\'.code -> append("\\\\")
                c == '('.code -> append("\\(")
                c == ')'.code -> append("\\)")
                c in 32..126 -> append(c.toChar())
                else -> append("\\").append(c.toString(8).padStart(3, '0'))
            }
        }
    }

    private fun latin1(s: String): ByteArray = ByteArray(s.length) { i ->
        val c = s[i].code
        (if (c in 0..255) c else '?'.code).toByte()
    }
}

/** Medidas de texto Helvetica / Helvetica-Bold (AFM, 1/1000 em). */
internal object PdfTexto {
    private val HELV = intArrayOf(
        278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
        556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
        1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
        667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
        333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
        556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584
    )
    private val HELV_B = intArrayOf(
        278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
        556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
        975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
        667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
        333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
        611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584
    )

    private fun baseChar(ch: Char): Char = when (ch) {
        'á', 'à', 'â', 'ã', 'ä' -> 'a'
        'Á', 'À', 'Â', 'Ã', 'Ä' -> 'A'
        'é', 'è', 'ê', 'ë' -> 'e'
        'É', 'È', 'Ê', 'Ë' -> 'E'
        'í', 'ì', 'î', 'ï' -> 'i'
        'Í', 'Ì', 'Î', 'Ï' -> 'I'
        'ó', 'ò', 'ô', 'õ', 'ö' -> 'o'
        'Ó', 'Ò', 'Ô', 'Õ', 'Ö' -> 'O'
        'ú', 'ù', 'û', 'ü' -> 'u'
        'Ú', 'Ù', 'Û', 'Ü' -> 'U'
        'ç' -> 'c'
        'Ç' -> 'C'
        'ñ' -> 'n'
        'Ñ' -> 'N'
        '“', '”' -> '"'
        '‘', '’', '´' -> '\''
        '–' -> '-'
        else -> ch
    }

    fun largura(s: String, size: Double, bold: Boolean): Double {
        val tab = if (bold) HELV_B else HELV
        var w = 0
        for (ch in s) {
            val c = baseChar(ch).code
            w += when {
                ch == '\u2013' -> 556
                c in 32..126 -> tab[c - 32]
                ch == '—' -> 1000
                else -> 556
            }
        }
        return w * size / 1000.0
    }

    fun truncar(s: String, maxW: Double, size: Double, bold: Boolean): String {
        if (largura(s, size, bold) <= maxW) return s
        var fim = s.length
        while (fim > 0 && largura(s.take(fim) + "...", size, bold) > maxW) fim--
        return s.take(fim) + "..."
    }

    fun wrap(text: String, maxW: Double, size: Double, bold: Boolean): List<String> {
        val out = mutableListOf<String>()
        text.replace('\r', ' ').split('\n').forEach { par ->
            var atual = ""
            par.split(Regex("\\s+")).filter { it.isNotEmpty() }.forEach { palavra ->
                val cand = if (atual.isEmpty()) palavra else "$atual $palavra"
                if (largura(cand, size, bold) <= maxW) {
                    atual = cand
                } else {
                    if (atual.isNotEmpty()) out += atual
                    atual = if (largura(palavra, size, bold) <= maxW) palavra else truncar(palavra, maxW, size, bold)
                }
            }
            if (atual.isNotEmpty()) out += atual
        }
        return out.ifEmpty { listOf("") }
    }
}
